//#region src/components/SignaturePad/signature-path.ts
/** The fixed coordinate space every signature is captured into. */
var SIGNATURE_VIEWBOX = {
	h: 200,
	w: 600
};
/** Points closer than this to the previously kept point are dropped. */
var MIN_POINT_DISTANCE = 1.5;
/** Ramer-Douglas-Peucker tolerance, in viewBox units. */
var DEFAULT_EPSILON = 1;
/** Upper bound on the emitted `d` string. */
var DEFAULT_MAX_BYTES = 8192;
/**
* Perpendicular distance from `point` to the infinite line through `a` and `b`.
* Falls back to point-distance when `a` and `b` coincide.
*/
function perpendicularDistance(point, a, b) {
	const dx = b[0] - a[0];
	const dy = b[1] - a[1];
	if (dx === 0 && dy === 0) return Math.hypot(point[0] - a[0], point[1] - a[1]);
	const cross = (point[0] - a[0]) * dy - (point[1] - a[1]) * dx;
	return Math.abs(cross) / Math.hypot(dx, dy);
}
/**
* Ramer-Douglas-Peucker simplification.
*
* Iterative rather than recursive on purpose: a pathological input (a very long
* sawtooth from a high-sample-rate stylus) can drive the recursive form to a
* stack depth of O(n), and this runs on user devices.
*/
function simplifyStroke(points, epsilon) {
	if (points.length <= 2) return points.slice();
	const keep = new Array(points.length).fill(false);
	keep[0] = true;
	keep[points.length - 1] = true;
	const stack = [[0, points.length - 1]];
	while (stack.length) {
		const [start, end] = stack.pop();
		let furthestIndex = -1;
		let furthestDistance = 0;
		for (let i = start + 1; i < end; i++) {
			const distance = perpendicularDistance(points[i], points[start], points[end]);
			if (distance > furthestDistance) {
				furthestDistance = distance;
				furthestIndex = i;
			}
		}
		if (furthestIndex !== -1 && furthestDistance > epsilon) {
			keep[furthestIndex] = true;
			stack.push([start, furthestIndex], [furthestIndex, end]);
		}
	}
	return points.filter((_, index) => keep[index]);
}
/** Drops points that sit within `MIN_POINT_DISTANCE` of the last kept point. */
function filterJitter(points) {
	const kept = [];
	for (const point of points) {
		const last = kept[kept.length - 1];
		if (!last || Math.hypot(point[0] - last[0], point[1] - last[1]) >= 1.5) kept.push(point);
	}
	return kept;
}
/** Emits `M x y` then `L x y` runs, absolute and integer-rounded. */
function toPathData(strokes) {
	return strokes.filter((stroke) => stroke.length).map((stroke) => stroke.map(([x, y], index) => `${index === 0 ? "M" : "L"}${Math.round(x)} ${Math.round(y)}`).join("")).join("");
}
/**
* Turns raw captured strokes into the stored `d` string.
*
* Pipeline: jitter filter -> RDP -> integer rounding -> join. If the result
* exceeds `maxBytes` the epsilon is doubled and the whole thing re-run, which
* bounds the worst case regardless of the device's sample rate. Once epsilon
* exceeds the viewBox diagonal RDP collapses every stroke to its two
* endpoints, so this always terminates well inside the attempt limit.
*/
function strokesToPathData(strokes, { epsilon = 1, maxBytes = DEFAULT_MAX_BYTES } = {}) {
	const filtered = strokes.map(filterJitter).filter((stroke) => stroke.length > 0);
	if (!filtered.length) return "";
	let currentEpsilon = epsilon;
	let d = toPathData(filtered.map((stroke) => simplifyStroke(stroke, epsilon)));
	for (let attempt = 0; attempt < 16 && d.length > maxBytes; attempt++) {
		currentEpsilon *= 2;
		d = toPathData(filtered.map((stroke) => simplifyStroke(stroke, currentEpsilon)));
	}
	if (d.length > maxBytes) {
		const simplified = filtered.map((stroke) => simplifyStroke(stroke, currentEpsilon));
		while (simplified.length > 1 && toPathData(simplified).length > maxBytes) simplified.pop();
		d = toPathData(simplified);
	}
	return d;
}
/**
* Inverse of {@link strokesToPathData}, so a pad can re-hydrate an existing
* value for editing. Malformed input yields no strokes rather than throwing.
*/
function parsePathData(d) {
	if (!d) return [];
	const strokes = [];
	let current;
	const commands = d.match(/[ML]-?\d+(?:\.\d+)? -?\d+(?:\.\d+)?/g);
	if (!commands) return [];
	for (const command of commands) {
		const [x, y] = command.slice(1).split(" ").map(Number);
		if (command[0] === "M") {
			current = [[x, y]];
			strokes.push(current);
		} else if (current) current.push([x, y]);
	}
	return strokes;
}
/** Type guard for a v1 signature value. Unknown versions are rejected. */
function isSignatureValue(value) {
	if (!value || typeof value !== "object") return false;
	const candidate = value;
	return candidate.v === 1 && typeof candidate.d === "string" && Number.isFinite(candidate.w) && Number.isFinite(candidate.h);
}
//#endregion
export { DEFAULT_EPSILON, DEFAULT_MAX_BYTES, MIN_POINT_DISTANCE, SIGNATURE_VIEWBOX, isSignatureValue, parsePathData, simplifyStroke, strokesToPathData };

//# sourceMappingURL=signature-path.js.map